import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '@theme';
import { useSession } from '@/contexts/SessionContext';
import { KajomLogo } from '@components/common/KajomLogo';
import { FiBell, FiChevronRight, FiHome, FiSearch } from 'react-icons/fi';
import * as DS from '@styles/DesignSystem.styles';
import NotificationBell from '@/components/layout/NotificationBell';

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.text}80;
  font-size: ${theme.typography.sizes.sm};
`;

const BreadcrumbItem = styled.span<{ isActive?: boolean }>`
  color: ${props => (props.isActive ? theme.colors.text : theme.colors.text + '60')};
  font-weight: ${props => (props.isActive ? theme.typography.weights.semibold : theme.typography.weights.normal)};
`;

const BreadcrumbSeparator = styled(FiChevronRight)`
  font-size: 14px;
  color: ${theme.colors.text}40;
`;

const HomeButton = styled.button`
  background: ${theme.colors.accent}10;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};
  color: ${theme.colors.accent};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};

  &:hover {
    background: ${theme.colors.accent};
    color: ${theme.colors.white};
  }

  svg {
    font-size: 16px;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.typography.weights.bold};
  font-size: ${theme.typography.sizes.base};
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const UserRole = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const UserInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    display: none;
  }
`;

const getBreadcrumbs = (pathname: string) => {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbMap: Record<string, string> = {
    dashboard: 'Tableau de bord',
    applications: 'Candidatures',
    'new-application': 'Nouvelle candidature',
    scoring: 'Scoring',
    users: 'Utilisateurs',
    billing: 'Facturation',
    settings: 'Paramètres',
    profile: 'Mon profil',
  };

  return paths.map(path => breadcrumbMap[path] || path);
};

export const Header: React.FC = () => {
  const { loggedUser: user } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  const breadcrumbs = getBreadcrumbs(location.pathname);

  // Déterminer si on est dans une route candidat
  const isCandidateRoute = location.pathname.startsWith('/candidate-home') ||
                           location.pathname.startsWith('/applications') ||
                           location.pathname.startsWith('/profile') ||
                           location.pathname.startsWith('/documents') ||
                           location.pathname.startsWith('/messages') ||
                           location.pathname.startsWith('/housing-search') ||
                           location.pathname.startsWith('/new-application');

  return (
    <DS.TopBar>
      <DS.TopBarIcon
        as="button"
        onClick={() => navigate('/candidate-landing')}
        title="Retour à l'accueil"
      >
        <FiHome />
      </DS.TopBarIcon>

      <DS.TopBarSearchContainer>
        <DS.TopBarSearchIcon>
          <FiSearch />
        </DS.TopBarSearchIcon>
        <DS.TopBarSearchInput
          type="text"
          placeholder="Rechercher une candidature, un candidat..."
        />
      </DS.TopBarSearchContainer>

      <DS.TopBarActions>
        <NotificationBell />

        <UserInfoWrapper>
          <UserAvatar>
            {user?.first_name?.charAt(0)}
            {user?.last_name?.charAt(0)}
          </UserAvatar>
          <UserDetails>
            <UserName>
              {user?.first_name} {user?.last_name}
            </UserName>
            <UserRole>{(user as any)?.display_role || user?.roles?.[0]}</UserRole>
          </UserDetails>
        </UserInfoWrapper>
      </DS.TopBarActions>
    </DS.TopBar>
  );
};
